import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import Logo from '@/assets/icons/logo.svg?react';
import { Button } from '@/components/ui/button';
import { Form, FormFieldPassword, FormFieldText, FormMessageRoot } from '@/components/ui/form';
import { Footnote, Heading3 } from '@/components/ui/typography';
import { auth } from '@/lib/firebase/client';
import { type ResetPassword, resetPasswordSchema } from '@/schemas/credentials';

const searchSchema = z.object({
  oobCode: z.string().optional(),
  tenantId: z.string().optional(),
});

const ResetPasswordComponent = ({
  setPasswordIsReset,
}: {
  setPasswordIsReset: (passwordIsReset: boolean) => void;
}) => {
  const { oobCode, tenantId } = useSearch({ from: '/(auth)/reset-password' });

  const form = useForm<ResetPassword>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      emailAddress: '',
      newPassword: '',
      repeatNewPassword: '',
    },
  });

  useEffect(() => {
    if (!oobCode) {
      toast.error('Geen reset code gevonden in de URL.', {
        position: 'bottom-right',
      });
      return;
    }

    if (tenantId) {
      auth.tenantId = tenantId;
    }

    verifyPasswordResetCode(auth, oobCode)
      .then((email) => form.setValue('emailAddress', email))
      .catch(() => {
        toast.error(
          'De link voor het opnieuw instellen van je wachtwoord is ongeldig of verlopen. Vraag een nieuwe resetlink aan.',
          {
            position: 'bottom-right',
          },
        );
      });
  }, []);

  const onSubmit = async (resetPassword: ResetPassword) => {
    if (!oobCode) return;

    try {
      await confirmPasswordReset(auth, oobCode, resetPassword.newPassword);
      setPasswordIsReset(true);
    } catch (e) {
      console.error('Could not confirm password reset. Error: ', e);
      setPasswordIsReset(false);
      form.setError('root', {
        type: 'manual',
        message: 'De reset link is ongeldig of verlopen.',
      });
    }
  };

  return (
    <div className="flex min-h-screen flex-row items-center justify-center">
      <div className="flex w-[317px] flex-col items-start justify-center gap-6">
        <Logo className="size-10" strokeWidth="1.5" />
        <Heading3>Nieuw wachtwoord instellen</Heading3>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex w-full flex-col items-start gap-4"
          >
            <FormFieldText
              control={form.control}
              name="emailAddress"
              label="Email"
              disabled
              inputStyle="rounded-input border border-input bg-muted py-2 pr-3 pl-4 w-full"
            />

            <FormFieldPassword
              control={form.control}
              name="newPassword"
              label="Nieuw wachtwoord"
              inputStyle="rounded-input border border-input bg-background py-2 pr-3 pl-4 w-full"
            />

            <FormFieldPassword
              control={form.control}
              name="repeatNewPassword"
              label="Bevestig nieuw wachtwoord"
              inputStyle="rounded-input border border-input bg-background py-2 pr-3 pl-4 w-full"
            />

            <FormMessageRoot />

            <div className="mt-2 flex w-full flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-center">
              <Button className="w-full" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting && <Loader2 className="animate-spin" />}
                Opslaan
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

const PasswordIsResetComponent = () => {
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen flex-row items-center justify-center">
      <div className="flex w-[317px] flex-col items-start justify-center gap-6">
        <Logo className="size-10" strokeWidth="1.5" />
        <Heading3>Wachtwoord gereset</Heading3>
        <div className="flex items-start gap-2 rounded-xl border border-border bg-background p-4">
          <Footnote className="text-foreground">
            Je wachtwoord is gereset en kunt nu inloggen met je emailadres en nieuwe wachtwoord.
          </Footnote>
        </div>
        <Button className="w-full px-3 py-2" onClick={() => navigate({ to: '/login' })}>
          Naar inloggen
        </Button>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/(auth)/reset-password')({
  component: ResetPasswordPage,
  validateSearch: (search) => searchSchema.parse(search),
});

function ResetPasswordPage() {
  const [passwordIsReset, setPasswordIsReset] = useState<boolean>(false);

  return passwordIsReset ? (
    <PasswordIsResetComponent />
  ) : (
    <ResetPasswordComponent setPasswordIsReset={setPasswordIsReset} />
  );
}
